import { Icon } from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import useSupplier from 'src/api/accounts-payable/useSupplier';
import { useCompanyBySupplier } from 'src/api/core/companies/useCompanyBySupplier';

interface SupplierPillProps {
  supplier?: {
    id: string;
    name: string;
    contactName?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  supplierId?: string;
  companyName?: string;
}

const SupplierPill: React.FC<SupplierPillProps> = ({
  supplier: providedSupplier,
  supplierId: supplierIdProp,
  companyName: providedCompanyName,
}) => {
  // Use provided supplierId, or fall back to supplier.id from the supplier object
  const supplierId = supplierIdProp || providedSupplier?.id;

  const { data: supplierData, isLoading } = useSupplier(
    supplierId,
    undefined,
    !!supplierId && !providedSupplier,
  );
  const { data: company, isLoading: isLoadingCompany } = useCompanyBySupplier(
    supplierId,
    !!supplierId && !providedCompanyName,
  );

  const supplier = (providedSupplier ||
    supplierData?.supplier) as SupplierPillProps['supplier'];

  // Use provided company name, or fetch from company data, or fallback to supplier name
  const displayName =
    providedCompanyName || company?.name || supplier?.name || 'Unknown';

  // Only show loading state if we don't have supplier data at all
  // If providedSupplier exists, we can show that while loading extra details (like company name)
  // We can show supplier name immediately and update to company name when it loads
  if ((isLoading && !providedSupplier) || !supplier) {
    return (
      <MDBox
        sx={{
          fontSize: 12,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          borderRadius: '16px',
          padding: '4px 8px',
          width: 'fit-content',
        }}
      >
        <MDTypography color="info.main" sx={{ fontSize: 12 }}>
          Loading...
        </MDTypography>
      </MDBox>
    );
  }

  const handlePillClick = (e: React.MouseEvent<HTMLElement>) => {
    if (supplierId && company?.id) {
      e.stopPropagation();
      e.preventDefault();
      window.open(`/companies/${company.id}`, '_blank');
    }
  };

  const handleContainerClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // Stop event from bubbling
  };

  // Handle middle-click (auxiliary click) to open company page in new tab
  const handleAuxClick = (e: React.MouseEvent<HTMLElement>) => {
    if (supplierId && company?.id && e.button === 1) {
      e.stopPropagation();
      e.preventDefault();
      window.open(`/companies/${company.id}`, '_blank');
    }
  };

  return (
    <>
      <MDBox
        display="flex"
        alignItems="center"
        onClick={handleContainerClick}
        sx={{ cursor: 'default' }}
      >
        <MDBox
          data-clickable-id={supplierId}
          display="flex"
          alignItems="center"
          gap={1}
          onClick={handlePillClick}
          onAuxClick={handleAuxClick}
          title={
            company?.id ? 'Click to open company details in new tab' : undefined
          }
          sx={{
            fontSize: 12,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            color: 'info.main',
            borderRadius: '16px',
            padding: '4px 8px',
            width: 'fit-content',
            cursor: supplierId ? 'pointer' : undefined,
            transition: 'all 0.2s ease-in-out',
            ...(supplierId && {
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.16)',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: 'none',
              },
            }),
          }}
        >
          <Icon sx={{ fontSize: 16, color: 'info.main' }}>business</Icon>
          <MDTypography color="info.main" sx={{ fontSize: 12 }}>
            {displayName}
          </MDTypography>
        </MDBox>
      </MDBox>
    </>
  );
};

export default SupplierPill;
